import React, { useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, SkeletonLoader } from '@sm/webassets';
import { Box } from '@wds/box';
import { t, defineMessages } from '@sm/intl';
import { ThemeProvider } from 'react-jss';
import theme from '~app/styles/cmsTheme';
import GetCollectorClosedByIdQuery from '~app/pages/SurveyClosed/GetCollectorClosedById.graphql';

import BrandedSurveyClosed from '~app/pages/SurveyClosed/BrandedSurveyClosed';
import BrandedEmbeddedSurveyClosed from '~app/pages/SurveyClosed/BrandedEmbeddedSurveyClosed';
import EUBrandedSurveyClosed from '~app/pages/SurveyClosed/EUBrandedSurveyClosed';
import NonBrandedSurveyClosed from '~app/pages/SurveyClosed/NonBrandedSurveyClosed';
import EmbeddedNonBrandedHeader from '~app/components/EmbeddedNonBrandedHeader';

const COPY = defineMessages({
  DEFAULT_CLOSED_MESSAGE: {
    id: 'SurveyClosed.closedMessage',
    defaultMessage:
      'This survey is currently closed.  Please contact the author of this survey for further assistance.',
  },
  META_KEYWORDS: {
    id: 'SurveyClosed.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  CLOSED_PAGE_TITLE: {
    id: 'SurveyClosed.title',
    defaultMessage: 'Free Online Survey Software by SurveyMonkey: Closed Survey',
  },
  CLOSED_PAGE_NON_BRANDED_TITLE: {
    id: 'SurveyClosed.nonBrandedTitle',
    defaultMessage: 'Online Survey Software: Closed Survey',
  },
});

const SurveyClosed = (): React.ReactElement => {
  const {
    pageOptions: { canonical, collectorId, isNonBrandedSmParam = false, isEmbeddedSmParam, languageId: surveyLangId },
    locale,
  } = useContext(StaticContext);
  const [showFooter, setShowFooter] = useState(!isNonBrandedSmParam || !isEmbeddedSmParam);

  const { loading, error, data } = useQuery(GetCollectorClosedByIdQuery, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': surveyLangId,
      },
    },
    variables: {
      collectorId,
      surveyLangId,
    },
    errorPolicy: 'all',
  });

  const nonBranded =
    isNonBrandedSmParam || data?.collector?.closedPage?.isBrandingDisabled || data?.collector?.hideBranding;
  const isEmbedded = isEmbeddedSmParam || data?.collector?.type?.toUpperCase() === 'POPUP';

  useEffect(() => {
    if (error || !data?.collector || nonBranded || isEmbedded) {
      setShowFooter(false);
    }
  }, [data, error, nonBranded, isEmbedded]);

  const title =
    !error && data?.collector && !nonBranded && !locale.isEUSubdomain
      ? t(COPY.CLOSED_PAGE_TITLE)
      : t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE);

  let childComponent: React.ReactElement;
  const closedMessage = data?.collector?.closedPage?.closedMessage ?? t(COPY.DEFAULT_CLOSED_MESSAGE);
  if (error || !data?.collector || (nonBranded && !isEmbedded)) {
    childComponent = <NonBrandedSurveyClosed message={closedMessage} />;
  } else if (isEmbedded && nonBranded) {
    childComponent = <EmbeddedNonBrandedHeader message={closedMessage} icon="closed" />;
  } else if (isEmbedded) {
    childComponent = <BrandedEmbeddedSurveyClosed message={closedMessage} />;
  } else if (locale.isEUSubdomain) {
    childComponent = <EUBrandedSurveyClosed message={closedMessage} />;
  } else {
    childComponent = <BrandedSurveyClosed message={closedMessage} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <BasePage includeHeader={false} includeFooter={showFooter} pageId="Survey Closed" legacyWeb="anonweb">
        <Helmet>
          <base target="_top" href="/" />
          <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
          <meta name="description" content={t(COPY.DEFAULT_CLOSED_MESSAGE)} />
          <link rel="canonical" href={canonical} />
        </Helmet>

        {loading && (
          <Box display="flex" flexJustify="center" flexAlign="center" p={5}>
            <SkeletonLoader height={130} width={400}>
              <circle cx="10" cy="20" r="8" />
              <rect x="25" y="15" rx="5" ry="5" width="350" height="10" />
            </SkeletonLoader>
          </Box>
        )}

        <Helmet title={title} />
        {childComponent}
      </BasePage>
    </ThemeProvider>
  );
};

export default SurveyClosed;
